import { useState, useEffect, useMemo } from 'react';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { Search, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import type { CropType } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';

interface CultivarFiltersProps {
  onFilterChange: (filters: CultivarFilterValues) => void;
  initialFilters?: CultivarFilterValues;
}

export interface CultivarFilterValues {
  search: string;
  crop_type_id: number | undefined;
  cultivar_role: '' | 'scion' | 'rootstock';
  status: string;
  protection_status: string;
}

const CULTIVAR_ROLE_OPTIONS = [
  { value: '', label: 'All Cultivar Types' },
  { value: 'scion', label: 'Scion' },
  { value: 'rootstock', label: 'Rootstock' },
] as const;

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'archived', label: 'Archived' },
];

const PROTECTION_OPTIONS = [
  { value: '', label: 'All Protection' },
  { value: 'protected', label: 'Protected (PBR/Patent)' },
  { value: 'pending', label: 'Pending' },
  { value: 'expired', label: 'Expired' },
  { value: 'open', label: 'Open/Unprotected' },
];

function CultivarFilters({ onFilterChange, initialFilters }: CultivarFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [cropTypes, setCropTypes] = useState<CropType[]>([]);
  const [filters, setFilters] = useState<CultivarFilterValues>({
    search: initialFilters?.search || '',
    crop_type_id: initialFilters?.crop_type_id,
    cultivar_role: initialFilters?.cultivar_role || '',
    status: initialFilters?.status || '',
    protection_status: initialFilters?.protection_status || '',
  });

  useEffect(() => {
    loadCropTypes();
  }, []);

  const loadCropTypes = async () => {
    try {
      const response = await cultivarService.getCropTypes();
      if (response.success && response.data) {
        setCropTypes(response.data);
      }
    } catch (error) {
      console.error('Failed to load crop types:', error);
    }
  };

  const handleFilterChange = (key: keyof CultivarFilterValues, value: string | number | undefined) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilterChange(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters: CultivarFilterValues = {
      search: '',
      crop_type_id: undefined,
      cultivar_role: '',
      status: '',
      protection_status: '',
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters = filters.search || filters.crop_type_id || filters.cultivar_role || filters.status || filters.protection_status;

  // Group crop types by parent so the dropdown can render <optgroup>s.
  const groupedCropTypes = useMemo(() => {
    const parents = cropTypes
      .filter((ct) => ct.parent_id === null)
      .sort((a, b) => a.name.localeCompare(b.name));
    return parents.map((parent) => ({
      parent,
      children: cropTypes
        .filter((ct) => ct.parent_id === parent.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [cropTypes]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-neutral-200">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-neutral-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-neutral-500" />
          <span className="font-semibold text-neutral-900">Filters</span>
          {hasActiveFilters && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
              Active
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-neutral-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-neutral-400" />
        )}
      </div>

      {/* Filter Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-neutral-100">
          {/* Search */}
          <form onSubmit={handleSearchSubmit} className="pt-4">
            <div className="relative">
              <Input
                placeholder="Search cultivars..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                icon={<Search className="h-4 w-4" />}
              />
            </div>
          </form>

          {/* Crop Type */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-neutral-700">
              Crop Type
            </label>
            <select
              value={filters.crop_type_id || ''}
              onChange={(e) => handleFilterChange('crop_type_id', e.target.value ? Number(e.target.value) : undefined)}
              className="input-professional w-full"
            >
              <option value="">All Crop Types</option>
              {groupedCropTypes.map(({ parent, children }) =>
                children.length === 0 ? (
                  <option key={parent.id} value={parent.id}>
                    {parent.name}
                  </option>
                ) : (
                  <optgroup key={parent.id} label={parent.name}>
                    <option value={parent.id}>All {parent.name}</option>
                    {children.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </optgroup>
                )
              )}
            </select>
          </div>

          {/* Cultivar Role */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-neutral-700">
              Scion / Rootstock
            </label>
            <select
              value={filters.cultivar_role}
              onChange={(e) => handleFilterChange('cultivar_role', e.target.value as CultivarFilterValues['cultivar_role'])}
              className="input-professional w-full"
            >
              {CULTIVAR_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-neutral-700">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input-professional w-full"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Protection Status */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-neutral-700">
              Protection Status
            </label>
            <select
              value={filters.protection_status}
              onChange={(e) => handleFilterChange('protection_status', e.target.value)}
              className="input-professional w-full"
            >
              {PROTECTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="w-full"
            >
              <X className="h-4 w-4 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default CultivarFilters;
